import React, { useEffect } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";

import { string } from "prop-types";

import Button from "../Button/Button";
import { formatCurrencyInRands } from "../../utils/randFomatter";
import { paymentReturnFor } from "../../utils/payment-return";

// These are Ozow's own status values. The pages used to compare against
// strings like "Payment Cancelled", which Ozow never sends, so every result
// fell through to "please wait while we process your payment" and stayed
// there while the raw status sat above it.
const OUTCOMES = {
  Complete: {
    tone: "success",
    Icon: CheckCircle2,
    heading: "Payment received",
    body: "Your account has been topped up. It can take a minute for your credit to reflect.",
  },
  Cancelled: {
    tone: "neutral",
    Icon: AlertTriangle,
    heading: "Payment cancelled",
    body: "Nothing was charged. You can try again whenever you are ready.",
    retry: true,
  },
  Abandoned: {
    tone: "neutral",
    Icon: AlertTriangle,
    heading: "Payment not completed",
    body: "The payment was not finished, so nothing was charged.",
    retry: true,
  },
  Error: {
    tone: "error",
    Icon: XCircle,
    heading: "Payment failed",
    body: "Something went wrong at the bank and the payment did not go through. Nothing was charged.",
    retry: true,
  },
  Pending: {
    tone: "pending",
    Icon: Clock,
    heading: "Payment pending",
    body: "Your bank has not confirmed this payment yet. Your credit will reflect once it does.",
  },
  PendingInvestigation: {
    tone: "pending",
    Icon: Clock,
    heading: "Payment under review",
    body: "This payment is being checked. Please give it a little time before trying again.",
  },
};

// A payment started from the landing page goes back to the client number on its
// own once complete, so an agent can move straight on to the next client.
const RETURN_AFTER_MS = 5000;

const UNKNOWN = {
  tone: "neutral",
  Icon: AlertTriangle,
  heading: "Payment status unavailable",
  body: "We could not read the result of this payment. Check your balance before trying again.",
  retry: true,
};

const PaymentOutcome = ({ defaultStatus }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  // The success route is only reached on a completed payment, so fall back
  // to that rather than telling a client who has just paid that we cannot
  // tell what happened.
  const status = params.get("Status") || defaultStatus;
  const reference = params.get("TransactionReference");
  const amount = parseFloat(params.get("Amount"));

  const outcome = OUTCOMES[status] || UNKNOWN;
  const { tone, Icon, heading, retry } = outcome;
  const isComplete = status === "Complete";
  const { returnTo, accessoryName } = paymentReturnFor(reference) || {};

  // The default wording talks about credit, which an accessory does not add.
  const body =
    accessoryName && status === "Complete"
      ? `Your ${accessoryName} has been paid for.`
      : accessoryName && status === "Pending"
        ? `Your bank has not confirmed this payment yet. Your ${accessoryName} will be recorded once it does.`
        : outcome.body;
  const autoReturn = isComplete && Boolean(returnTo);

  useEffect(() => {
    if (!autoReturn) {
      return undefined;
    }
    const timer = setTimeout(() => navigate(returnTo), RETURN_AFTER_MS);
    return () => clearTimeout(timer);
  }, [autoReturn, returnTo, navigate]);

  return (
    <div className="payment-outcome">
      <div className={`payment-outcome__badge payment-outcome__badge--${tone}`}>
        <Icon size={32} />
      </div>

      <div className="payment-outcome__heading">{heading}</div>
      <p className="payment-outcome__body">{body}</p>

      {(!isNaN(amount) || reference) && (
        <div className="payment-outcome__details">
          {!isNaN(amount) && (
            <div className="payment-outcome__detail">
              <span>Amount</span>
              <span>{formatCurrencyInRands(amount)}</span>
            </div>
          )}
          {reference && (
            <div className="payment-outcome__detail">
              <span>Reference</span>
              <span>{reference}</span>
            </div>
          )}
        </div>
      )}

      {autoReturn && (
        <p className="payment-outcome__body">
          Taking you back in a few seconds.
        </p>
      )}

      {returnTo ? (
        <div className="payment-outcome__actions">
          <Button
            onClick={() => navigate(returnTo)}
            name={retry ? "Try again" : "Done"}
            className={"button--primary"}
          />
        </div>
      ) : (
        <div className="payment-outcome__actions">
          {retry && (
            <Button
              onClick={() => navigate("/top-up")}
              name={"Try again"}
              className={"button--primary"}
            />
          )}
          <Button
            onClick={() => navigate("/my-profile")}
            name={retry ? "Back to my profile" : "Done"}
            className={retry ? "button--secondary" : "button--primary"}
          />
        </div>
      )}
    </div>
  );
};

PaymentOutcome.propTypes = {
  defaultStatus: string,
};

export default PaymentOutcome;
